import { Breakpoint } from '@procsea/design-system'

export enum TrackingType {
  FILTER = 'Filter',
  CTA = 'CTA',
  FORM_FIELD = 'FormField',
}

export enum TrackingPersona {
  BUYER = 'buyer',
  CENTRAL = 'central',
  SELLER = 'seller',
}

export enum TrackingSource {
  CLAIM_LIST = 'claim_list',
  CREDIT_NOTE_LIST = 'credit_note_list',
  CUSTOM_CATALOG = 'custom_catalog',
  DIRECT_ORDER = 'direct_order',
  INVOICE_LIST = 'invoice_list',
  INVOICE_TO_GENERATE_LIST = 'invoice_to_generate_list',
  LINE_MANAGEMENT = 'line_management',
  AI_INVOICE_PROCESSING = 'ai_invoice_processing',
  ASSORTMENT_MANAGEMENT = 'assortment_management',
  LIVE_MARKET = 'live_market',
  ORDER_LIST = 'order_list',
  PRICE_SHEET_LIST = 'price_sheet_list',
  PRODUCT_MAPPING_SLIDING_PANEL = 'product_mapping_sliding_panel',
  PURCHASE_ORDER_LIST = 'purchase_order_list',
  SCHEDULER = 'scheduler',
  SELLER_LIVE_CATALOG = 'seller_live_catalog',
  STEF_DELIVERY_INFORMATION = 'stef_delivery_information',
  STOCK_MANAGEMENT = 'stock_management',
  UNKNOWN = 'unknown',
  PREPARATION_PAGE = 'preparation_page',
  MAIN_MENU = 'main_menu',
  PACKING = 'packing',
  BUYER_TENDERS = 'buyer_tenders',
  BUYER_TENDER_RESPONSES = 'buyer_tender_responses',
  SELLER_TENDERS = 'seller_tenders',
  TENDERS = 'tenders',
}

export interface TrackingConfig {
  breakpoint: Breakpoint
  persona: TrackingPersona
  source: TrackingSource
}

export type UseTrackingArgs = Pick<TrackingConfig, 'source'> &
  Partial<Pick<TrackingConfig, 'persona'>>

export interface UseTrackingResult {
  trackCta: (payload: TrackCtaArgs) => void
  trackFilter: (payload: TrackFilterArgs) => void
  trackFormField: (payload: TrackFormFieldArgs) => void
}

export type TrackFilterValue =
  | boolean
  | string
  | string[]
  | readonly string[]
  | number
  | number[]
  | readonly number[]
  | null
  | undefined

export interface TrackFilterArgs extends Partial<TrackingConfig> {
  filterName: string
  filterValue: TrackFilterValue
  verticals?: string[]
}

export enum CtaAction {
  ADD_BATCH = 'add_batch',
  ADD_PARCEL = 'add_parcel',
  ADD_TO_COMPARISON = 'add_to_comparison',
  ALLOCATE_FROM_COMPARISON = 'allocate_from_comparison',
  CANCEL_ORDER = 'cancel_order',
  CANCEL_ORDER_ITEM = 'cancel_order_item',
  CHALLENGE_TENDER_RESPONSE_PRODUCT = 'challenge_tender_response_product',
  CHECK_OPEN_DOWNLOAD_MODAL = 'check_open_download_modal',
  CLOSE_COMPARISON = 'close_comparison',
  CLOSE_TENDER = 'close_tender',
  CREATE_CUSTOMER_CATEGORY_DISCOUNT_EXCEPTION = 'create_customer_category_discount_exception',
  CREATE_CUSTOMER_DISCOUNT_EXCEPTION = 'create_customer_discount_exception',
  CREATE_VIEW = 'create_view',
  DELETE_BATCH = 'delete_batch',
  DELETE_CATALOG_PRODUCT_MAPPING = 'delete_catalog_product_mapping',
  DELETE_CUSTOMER_CATEGORY_DISCOUNT_EXCEPTION = 'delete_customer_category_discount_exception',
  DELETE_CUSTOMER_DISCOUNT_EXCEPTION = 'delete_customer_discount_exception',
  DELETE_PARCEL = 'delete_parcel',
  DELETE_VIEW = 'delete_view',
  DISMISS_MAPPING_POPOVER = 'dismiss_mapping_popover',
  DOWNLOAD_DELIVERY_NOTICE = 'download_delivery_notice',
  DOWNLOAD_PROFORMA_DOCUMENT = 'download_proforma_document',
  DOWNLOAD_SSCC_LABEL = 'download_sscc_label',
  DOWNLOAD_STEF_DELIVERY_DOCUMENT = 'download_stef_delivery_document',
  DOWNLOAD_STEF_PROOF_OF_DEVELIVERY = 'download_stef_proof_of_develivery',
  DOWNLOAD_TRANSPORT_LABEL = 'download_transport_label',
  DUPLICATE_OFFER_AND_MAP_TO_CATALOG = 'duplicate_offer_and_map_to_catalog',
  DUPLICATE_OFFER_AS_DRAFT_AND_MAP_TO_CATALOG = 'duplicate_offer_as_draft_and_map_to_catalog',
  DUPLICATE_VIEW = 'duplicate_view',
  EDIT_VIEW = 'edit_view',
  EXPRESS_INTEREST_IN_INVOICING_WITH_AI = 'express_interest_in_invoicing_with_ai',
  GENERATE_ALLOCATION_WITH_AI = 'generate_allocation_with_ai',
  GO_TO_EDIT_ORDER_PAGE = 'go_to_edit_order_page',
  MAP_TO_CATALOG_PRODUCT = 'map_to_catalog_product',
  MAP_TO_DRAFT_CATALOG_PRODUCT = 'map_to_draft_catalog_product',
  NO_PRODUCT_MATCH_THE_OFFER = 'no_product_match_the_offer',
  OPEN_COMPARISON = 'open_comparison',
  OPEN_EDIT_ORDER_ITEM_PANEL = 'open_edit_order_item_panel',
  OPEN_MAPPING_POPOVER = 'open_mapping_popover',
  OPEN_MAPPING_SLIDING_PANEL = 'open_mapping_sliding_panel',
  OPEN_OFFER_DUPLICATION_SLIDING_PANEL = 'open_offer_duplication_sliding_panel',
  OPEN_PARCELS_MODAL = 'open_parcels_modal',
  REDUCE_COMPARISON = 'reduce_comparison',
  REMOVE_FROM_COMPARISON = 'remove_from_comparison',
  RESTORE_VIEW = 'restore_view',
  SAVE = 'save',
  SAVE_AS_NEW_VIEW = 'save_as_new_view',
  SAVE_ORDER_ITEM = 'save_order_item',
  SAVE_VIEW = 'save_view',
  SEE_MORE_COMPARISON = 'see_more_comparison',
  SELECT_VIEW = 'select_view',
  UPDATE_CUSTOMER_CATEGORY_DISCOUNT_EXCEPTION = 'update_customer_category_discount_exception',
  UPDATE_CUSTOMER_DISCOUNT_EXCEPTION = 'update_customer_discount_exception',
  VALIDATE = 'validate',
  VALIDATE_AND_INVOICE = 'validate_and_invoice',
  VALIDATE_AND_PACKING = 'validate_and_packing',
  VALIDATE_PARCELS = 'validate_parcels',
  SEND_WEBHOOK = 'send_webhook',
  SEND_WEBHOOK_BATCH = 'send_webhook_batch',
  FINALIZE_TENDER = 'finalize_tender',
  SAVE_ALLOCATIONS = 'save_allocations',
  EXPORT_ALLOCATIONS = 'export_allocations',
  SUBMIT_TENDER_RESPONSE = 'submit_tender_response',
  OPEN_SUPPLIER_FOLLOW_UPS = 'open_supplier_follow_ups',
  BULK_CLOSE_TENDERS = 'bulk_close_tenders',
}

export interface TrackCtaArgs extends Partial<TrackingConfig> {
  action: CtaAction
  label: string | null
  [key: string]: any
}

export interface TrackFormFieldArgs extends Partial<TrackingConfig> {
  formFieldName: string
  value: any
  [key: string]: any
}

export const breakpointMapper: Record<Breakpoint, string> = {
  [Breakpoint.IS_MOBILE]: 'mobile',
  [Breakpoint.IS_TABLET]: 'tablet',
  [Breakpoint.IS_SMALL_DESKTOP]: 'small_desktop',
  [Breakpoint.IS_MEDIUM_DESKTOP]: 'medium_desktop',
  [Breakpoint.IS_LARGE_DESKTOP]: 'large_desktop',
  [Breakpoint.IS_DESKTOP]: 'desktop',
}

export const filterNameMapper: Record<string, string> = {
  q: 'search',
  q_items: 'secondary_search',
}
